import { InfoOutlined } from "@mui/icons-material";
import { useForm } from "react-final-form";
import RffBooleanCheckbox from "../../components/finalForm/inputs/RffBooleanCheckbox";
import RffDateField from "../../components/finalForm/inputs/RffDateField";
import RffDollarField from "../../components/finalForm/inputs/RffDollarField";
import RffFloatField from "../../components/finalForm/inputs/RffFloatField";
import RffSelectAutocompleteField from "../../components/finalForm/inputs/RffSelectAutocompleteField";
import RffSelectField from "../../components/finalForm/inputs/RffSelectField";
import RffExitStrategyField from "../../components/finalForm/inputs/RffExitStrategyField";
import RffTextField from "../../components/finalForm/inputs/RffTextField";
import RffCheckboxGroup from "../../components/finalForm/shared/RffCheckboxGroup";
import RffConditional from "../../components/finalForm/shared/RffConditional";
import RffGroup from "../../components/finalForm/shared/RffGroup";
import TitledCard from "../../components/ui/TitledCard/TitledCard";
import { selectOptionsLoanProductType } from "../../constants/selectOptions/selectOptionsLoanProductType";
import { selectOptionsLoanPurpose } from "../../constants/selectOptions/selectOptionsLoanPurpose";
import RffBooleanToggleField from "../../components/finalForm/inputs/RffBooleanToggleField";
import { rffDependentGroupStyles } from "../../styles/rffDependentGroupStyles";
import { useCorrespondentOptions } from "../../hooks/useCorrespondentOptions";
import { useIsAcquisitions } from "../../pipelines/PipelineContext";
import {
  CASH_OUT_REFI,
  DELAYED_PURCHASE_FIELD,
  DELAYED_PURCHASE_HINT,
  DELAYED_PURCHASE_LABEL,
  canBeDelayedPurchase,
} from "../../utils/loans/delayedPurchase";
import {
  canBeDelayedPurchaseCondition,
  trueRefinanceCondition,
} from "../../utils/loans/delayedPurchaseConditions";

const LoanDetailSection = ({ testing, defaultExpanded }) => {
  // Correspondent objects (id → companyName) from the cached /correspondents/list.
  const correspondentOptions = useCorrespondentOptions();
  const isAcquisitions = useIsAcquisitions();
  const { change } = useForm();

  // RffConditional only HIDES the other date; the value stays in the form. On a
  // not-closed loan a leftover originationDate is not harmless: the backend's
  // R0 router treats "originationDate is set" as closed evidence on its own, so
  // the loan would be routed to full diligence against a close that never
  // happened. Clear it the moment the answer flips to No.
  const onLoanClosedChange = (closed) => {
    if (closed === false) change("originationDate", undefined);
  };
  // The delayed-purchase flag only means anything on a cash-out refinance (the
  // backend clears it otherwise) — drop a hidden leftover answer when the
  // purpose moves away, so the payload never carries a contradiction.
  const onLoanPurposeChange = (purpose) => {
    if (!canBeDelayedPurchase(purpose)) change(DELAYED_PURCHASE_FIELD, undefined);
  };
  return (
    <TitledCard
      icon={<InfoOutlined className="thin" />}
      variant="h1"
      defaultExpanded={defaultExpanded}
      title="Loan Details"
      collapsible
    >
      <RffGroup
        suppressBottomMargin
        //
        // title="Loan Details"
      >
        {/* <RffSelectUserEmailField
          size={4}
          name="loanProcessor"
          name="processor"
          label="Loan Processor"
        />
        <RffSelectUserEmailField name="loanUnderwriter" label="Underwriter" /> */}

        <RffSelectField
          name="loanProductType"
          label="Loan Product Type"
          options={selectOptionsLoanProductType}
          size={6}
          required
        />
        <RffSelectField
          name="loanPurpose"
          label="Loan Purpose"
          size={6}
          options={selectOptionsLoanPurpose}
          required
          onChange={onLoanPurposeChange}
        />
        {/* Delayed purchase is a flag on a cash-out refinance (REQ-106), not a
            purpose of its own — asked only when it can apply. */}
        <RffConditional
          testing={testing}
          condition={canBeDelayedPurchaseCondition()}
        >
          <RffBooleanToggleField
            name={DELAYED_PURCHASE_FIELD}
            label={DELAYED_PURCHASE_LABEL}
            helperText={DELAYED_PURCHASE_HINT}
            size={6}
          />
        </RffConditional>
        {/* Preclose only — on acquisitions the closing date lives in the Loan
            Closed? row at the bottom of the card. */}
        {isAcquisitions ? null : (
          <RffDateField
            size={6}
            name="expectedClosingDate"
            label="Expected Closing Date"
          />
        )}

        {/* Required on both intake paths (preclose and acquisitions — the same
            screen, wrapped in a different PipelineProvider): a loan with no
            correspondent can't be scoped to one downstream, and backfilling it
            later means finding it first. */}
        <RffSelectAutocompleteField
          name="correspondentCompanyId"
          label="Correspondent"
          options={correspondentOptions}
          // Nothing sits beside it on acquisitions, so it takes the row.
          size={isAcquisitions ? 12 : 6}
          alphabetize
          required
        />

        <RffTextField
          name="box_correspondent_folder_url"
          label="Box Correspondent Folder URL"
        />
        {/* Exit Strategy options are scoped to the selected Loan Product Type.
            On acquisitions it moves into the Loan Closed? row below (2 | 5 | 5:
            toggle, date, exit strategy). */}
        {isAcquisitions ? null : (
          <RffExitStrategyField name="exitStrategy" label="Exit Strategy" />
        )}

        {/* ACQUISITIONS ONLY — is this a purchased (already-closed) loan or an
            expected trade locked before it closed? The backend's R0 router
            (workflows/stages/acquisition_state.py) reads `loanClosed` plus
            whichever date is set to decide full diligence vs the lighter
            advisory review, and `originationDate` is the D5 anchor every
            document window is graded against on a closed loan — so the answer
            has to be explicit at intake, with the matching date beside it.
            Unanswered reads as "not closed" (the model default is False), so
            the estimated date shows until someone says Yes. Preclose loans
            never close at creation, so the row is hidden there. */}
        {isAcquisitions ? (
          <>
            <RffBooleanToggleField
              name="loanClosed"
              label="Loan Closed?"
              size={2}
              required
              onChange={onLoanClosedChange}
            />
            <RffConditional field="loanClosed" operator="!=" value={true}>
              <RffDateField
                size={5}
                name="expectedClosingDate"
                label="Estimated Closing Date"
                required
              />
            </RffConditional>
            <RffConditional field="loanClosed" value={true}>
              <RffDateField
                size={5}
                name="originationDate"
                label="Origination / Note Date"
                helperText="The date the note was signed at the correspondent's close"
                required
              />
            </RffConditional>
            <RffExitStrategyField
              name="exitStrategy"
              label="Exit Strategy"
              size={5}
            />
          </>
        ) : null}

        <RffConditional field="loanProductType" value="DSCR">
          <RffFloatField
            name="dscr"
            //
            label="The DSCR for the loan as measured by counterparty"
            // label="DSCR"
          />
          <RffDollarField
            name="totalInPlaceRents"
            size={6}
            label="Total In Place Rents"
            helperText="The total rents for the loan for all properties and all units per property"
          />
          <RffDollarField
            name="totalMarketRents"
            size={6}
            label="Total Market Rents"
            helperText="The total market rents for the loan for all properties and all units per property"
          />
        </RffConditional>

        <RffConditional
          testing={testing}
          operatorForMultiple={"OR"}
          rules={[
            {
              field: "loanProductType",
              operator: "==",
              value: "Stabilized Bridge",
            },
            {
              field: "loanProductType",
              operator: "==",
              value: "30-Year Rental Loan",
            },
            {
              field: "loanProductType",
              operator: "==",
              value: "Other",
            },
            {
              field: "loanProductType",
              operator: "==",
              value: "Foreclosure Bail Out",
            },
            {
              field: "loanProductType",
              operator: "==",
              value: "DSCR",
            },
          ]}
        >
          <RffDollarField name="baseLoanAmount" label="Requested Loan Amount" />
        </RffConditional>

        <RffConditional
          testing={testing}
          operatorForMultiple={"OR"}
          rules={[
            {
              field: "loanProductType",
              operator: "==",
              value: "Fix and Flip",
            },
            {
              field: "loanProductType",
              operator: "==",
              value: "Ground Up Construction",
            },
            {
              field: "loanProductType",
              operator: "==",
              value: "Vacant Land",
            },
          ]}
        >
          <RffDollarField
            size={4}
            name="baseLoanAmount"
            label="Initial loan amount"
            helperText="Amount before any draws"
          />
          <RffDollarField
            size={4}
            name="totalHoldback"
            label="Total financed budget"
            helperText="Total amount requested via draws"
          />
          {/* Render this only when totalHoldback < totalConstructionBudget */}
          <RffDollarField
            size={4}
            name="totalConstructionBudget"
            label="Total construction budget"
            helperText="The full project cost (if draws cover less than 100% of it)"
          />
          {/* <RffDollarField
            name="baseLoanAmount"
            label="Requested initial loan amount (baseLoanAmount)"
            helperText={"Financed amount before any financed budget"}
          />
          <RffDollarField
            name="totalHoldback"
            label="Requested financed budget amount"
          />
          <RffDollarField
            name="totalConstructionBudget"
            label="If the financed budget is less than 100% of the total construction budget for the project, please enter the total budget"
            helperText={
              "The total amount of the construction budget to be financed through draws"
            }
          /> */}
        </RffConditional>
      </RffGroup>
      <RffConditional
        testing={testing}
        field="loanPurpose"
        operator="includes"
        value={["Refinance - No Cash Out", CASH_OUT_REFI]}
      >
        <RffGroup
          sx={{ mt: 6 }}
          groupLabel="Refinanced Details"
          suppressBottomMargin
        >
          <RffConditional
            testing={testing}
            field="loanPurpose"
            operator="includes"
            value={["Refinance - No Cash Out", CASH_OUT_REFI]}
          >
            <RffDollarField
              name="outstandingLoanUpb"
              label="Total principal amount of outstanding loan"
              helperText="(This would be your expected payoff)"
            />

            <RffTextField
              size={6}
              name="exisitingLender"
              label="What is the name of the current lender?"
            />
            <RffDateField
              size={6}
              name="existingLoanMaturityDate"
              label="Maturity date of existing loan :"
            />

            {/* EXISTING LOAN & PROPERTY STATUS — one disclosure checklist.
                The default / undrawn-budget items reveal a follow-up field
                when checked. Property-listed-in-last-6-months only applies to
                a true refinance — not a delayed purchase (cash-out with the flag
                set, or a legacy "Delayed Purchase" loan). */}
            <RffCheckboxGroup label="Check all that apply to the existing loan and property:">
              <RffBooleanCheckbox
                name="existingLoanLatePayments"
                label="Payments have been made more than 30 days past the due date on the existing loan"
              />
              <RffBooleanCheckbox
                name="existingLoanInDefault"
                label="The existing loan is in default (payment or maturity default)"
              />
              <RffConditional
                testing={testing}
                field="existingLoanInDefault"
                value={true}
              >
                <RffGroup
                  disableGrid
                  isGridChild
                  groupLabel="Existing loan default type"
                  sx={rffDependentGroupStyles}
                >
                  <RffSelectField
                    suppressGrid
                    name="existingLoanDefaultType"
                    label="Select existing loan default type:"
                    options={["Maturity Default", "Payment Default"]}
                  />
                </RffGroup>
              </RffConditional>
              <RffBooleanCheckbox
                name="existingLoanHasUndrawnBudget"
                label="The existing loan has an undrawn budget"
              />
              <RffConditional
                testing={testing}
                field="existingLoanHasUndrawnBudget"
                value={true}
              >
                <RffGroup
                  disableGrid
                  isGridChild
                  groupLabel="Undrawn budget amount"
                  sx={rffDependentGroupStyles}
                >
                  <RffDollarField
                    suppressGrid
                    name="existingLoanUndrawnBudgetAmount"
                    label="Existing loan undrawn budget amount"
                  />
                </RffGroup>
              </RffConditional>
              <RffBooleanCheckbox
                name="propertyListedForSale"
                label="The property is currently listed for sale"
              />
              <RffConditional
                testing={testing}
                condition={trueRefinanceCondition()}
              >
                <RffBooleanCheckbox
                  name="propertyListedForSaleLastSixMonths"
                  label="The property has been listed for sale in the last six months"
                />
              </RffConditional>
            </RffCheckboxGroup>
          </RffConditional>
        </RffGroup>
      </RffConditional>
    </TitledCard>
  );
};

export default LoanDetailSection;
